import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { alert } from 'actions/flash'
import icCopy from 'assets/images/ic-copy.svg'
import Input from 'components/Form/Input'
import ModalContainer from 'components/ModalContainer'
import palette from 'constants/palette'
import Form from 'formsy-react'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useStyletron } from 'styletron-react'

const t = buildTranslate('components.send_message_modal')
const validationT = buildTranslate('validations')

// Mirrors the backend caps: MESSAGE_SUBJECT_MAX_LENGTH (both
// api/v1/recovery/conversations.rb and api/v1/order_ratings.rb) and
// OPERATOR_NOTE_MAX_LENGTH, reused as the message body cap on both endpoints.
const SUBJECT_MAX_LENGTH = 200
const MESSAGE_MAX_LENGTH = 2000

// Textarea height — tall enough for a short paragraph without pushing the
// dialog's actions below the fold (mirrors ResolveWithReplyModal's REPLY_ROWS).
const MESSAGE_ROWS = 6

// Wired via aria-describedby on the disabled Copy button (Base Web Button
// overrides — direct aria-* props don't reach the underlying <button>, see
// CaseDetail's restrict-button precedent) so a screen-reader user lands on
// WHY it's disabled, not just that it is.
const EMAIL_REASON_ID = 'send-message-modal-email-reason'

export type SendMessagePayload = {
  subject: string
  message: string
}

// The recipient's email is FOUR distinct states, not just present/null — a bare
// `string | null` conflated "we don't have the address" with "the customer has
// no address," which wrongly blocked a legitimate send for the common cases:
//   known   — the FE has the address; show it, allow Copy, allow Send.
//   masked  — the record HAS a recipient the backend can resolve server-side,
//             but this operator can't see the address (RAI PII-consent gate, or
//             an unfetchable/errored profile on a rating that still has a user).
//             Copy has nothing to show, but Send still delivers — so it's ALLOWED.
//   loading — the address fetch is still in flight (rating profile). Transient:
//             render a loading state, not the "no email" error, and hold Send
//             until it settles.
//   absent  — genuinely no linked recipient (no user on the record). Send is
//             impossible; this is the ONLY state that permanently blocks it.
export type CustomerEmailState =
  | { status: 'known'; value: string }
  | { status: 'masked' }
  | { status: 'loading' }
  | { status: 'absent' }

type Props = {
  isOpen: boolean
  // See CustomerEmailState: only `absent` (and, transiently, `loading`) blocks
  // Send — a `masked` recipient still delivers because the backend resolves the
  // address server-side, so the FE not being able to SEE it must not gate it.
  customerEmail: CustomerEmailState
  customerName: string | null
  // Whether a successful send ALSO resolves the underlying record. The email
  // itself is identical either way — this only drives the caller-honest copy
  // (confirm button + a body note), since the shared modal has two callers with
  // divergent side effects: RecoveryAI's CaseDetail resolves the case on send
  // (backend send-and-resolve), while the Order Feedback rating detail is
  // genuinely send-only. Defaults to send-only so the Order Feedback surface is
  // unaffected. The success flash is owned by each caller, not this component.
  resolvesRecord?: boolean
  // The LOGGED-IN OPERATOR's email (not the customer's, above) — replies to the
  // message land there, because the BE sets it as the Reply-To on the send-only
  // email (FeedbackOperatorMessage / RecoveryOperatorMessage #reply_to_address).
  // Independent of `customerEmail`: a masked recipient still gets a footer,
  // since Reply-To doesn't depend on whether this operator may see who they're
  // writing to. Null when the current merchant user isn't in the store yet,
  // which hides the footer rather than rendering a half-empty sentence. Same
  // contract as ResolveWithReplyModal / GrantRewardModal.
  operatorEmail: string | null
  // The caller's mutation state. onSend resolves on success and rejects on
  // failure; this component closes itself on the former and leaves the
  // caller's isError to render the banner on the latter — mirrors
  // ResolveWithReplyModal / GrantRewardModal.
  onSend: (payload: SendMessagePayload) => Promise<unknown>
  isSubmitting: boolean
  isError: boolean
  onResetError: () => void
  // Surface-specific failure copy, so this shared component never hardcodes
  // one caller's wording (mirrors ResolveWithReplyModal).
  errorMessage: string
  onClose: () => void
}

// Operator action: email the customer a subject + message. The SIDE EFFECT is
// caller-defined via `resolvesRecord`: send-only for the Order Feedback rating
// detail, or send-and-resolve for RecoveryAI's CaseDetail (the backend closes
// the case on send). Shared by both; each owns its own mutation and passes it
// in via onSend, and owns its own success flash. Reuses the shared
// ModalContainer and mirrors ResolveWithReplyModal's established behaviours
// (close-guard while submitting, full reset on close, inline error banner).
const SendMessageModal = ({
  isOpen,
  customerEmail,
  customerName,
  resolvesRecord = false,
  operatorEmail,
  onSend,
  isSubmitting,
  isError,
  onResetError,
  errorMessage,
  onClose,
}: Props) => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  // Formsy's verdict on the two fields' maxLength rules, which gates Send the
  // same way ResolveWithReplyModal's reply field does.
  const [isFormValid, setIsFormValid] = useState(true)
  // components/Form/Input is UNCONTROLLED (renders `defaultValue`), so clearing
  // `subject`/`message` alone leaves the typed text in the DOM of fields still
  // on screen. Bumping this key remounts them — see ResolveWithReplyModal for
  // the full rationale (same pattern, same reason).
  const [resetCount, setResetCount] = useState(0)

  // Copy needs a visible address; only `known` has one.
  const canCopy = customerEmail.status === 'known'
  // Send may proceed for a known OR masked recipient — the backend resolves the
  // address server-side, so a hidden one still delivers. `loading`/`absent` block.
  const hasRecipient =
    customerEmail.status === 'known' || customerEmail.status === 'masked'
  // Required (unlike ResolveWithReplyModal's optional reply): both fields must
  // carry real content, a recipient must be resolvable, nothing already in
  // flight, and neither field can be over-length.
  const canProceed =
    isFormValid &&
    !isSubmitting &&
    hasRecipient &&
    subject.trim().length > 0 &&
    message.trim().length > 0

  const resetDraft = useCallback(() => {
    setSubject('')
    setMessage('')
    setIsFormValid(true)
    setResetCount(count => count + 1)
  }, [])

  // Reset on the CLOSED edge, not only in handleClose — mirrors
  // ResolveWithReplyModal (see that component for the full rationale: the
  // calling scenes close this directly on a record-change effect, which never
  // runs handleClose, and this component outlives Base Web's unmount of the
  // dialog contents).
  useEffect(() => {
    if (isOpen) return
    resetDraft()
  }, [isOpen, resetDraft])

  const handleClose = () => {
    // Ignore close requests while a send is in flight — the POST can't be
    // aborted client-side (mirrors ResolveWithReplyModal / GrantRewardModal).
    if (isSubmitting) return
    resetDraft()
    onResetError()
    onClose()
  }

  const handleSend = () => {
    if (!canProceed) return
    onSend({ subject: subject.trim(), message: message.trim() })
      .then(handleClose)
      // The failure is surfaced inline via isError below; swallow the
      // rejection so it isn't an unhandled promise.
      .catch(() => {})
  }

  const handleCopy = async () => {
    if (customerEmail.status !== 'known') return

    // navigator.clipboard needs a secure context and writeText rejects on a
    // denied permission / unfocused document — confirm only on an actual write,
    // and say so on failure rather than leaving the operator believing they
    // copied something (mirrors Activation's useActivationLink#copyLink).
    try {
      await navigator.clipboard.writeText(customerEmail.value)
    } catch {
      dispatch(
        alert({ key: 'danger', message: t('email_copy_failed'), timeout: 5 })
      )
      return
    }
    dispatch(alert({ key: 'success', message: t('email_copied'), timeout: 5 }))
  }

  const title = customerName ? t('title', { name: customerName }) : t('heading')

  // Field text mirrors the four recipient states; only `known` renders the real
  // address (grey90), the rest are grey70 placeholder copy. Early-return helper
  // rather than a nested ternary (conventions.md).
  const emailFieldText = (): string => {
    switch (customerEmail.status) {
      case 'known':
        return customerEmail.value
      case 'masked':
        return t('masked_email')
      case 'loading':
        return t('loading_email')
      case 'absent':
        return t('no_email')
    }
  }

  // The explanatory line under the field (also the Copy button's
  // aria-describedby target). Only the two terminal, actionable states carry
  // one — `loading` is transient and the field itself reads "Loading…".
  const disabledReason = (): string | null => {
    switch (customerEmail.status) {
      case 'masked':
        return t('masked_reason')
      case 'absent':
        return t('no_email_reason')
      default:
        return null
    }
  }
  const reason = disabledReason()

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      proceedText={resolvesRecord ? t('send_and_resolve') : t('send')}
      onProceed={handleSend}
      canProceed={canProceed}
      isLoading={isSubmitting}
    >
      <div className="mt-m">
        <Text.BodyText4 bold tag="div" color="grey90" className="mb-xs">
          {t('customer_email_label')}
        </Text.BodyText4>
        <div
          className={`d-flex align-items-center justify-content-between ${css({
            border: `1px solid ${palette.grey40}`,
            borderRadius: '4px',
            padding: '12px 16px',
            backgroundColor: palette.grey10,
          })}`}
        >
          <Text.BodyText4
            tag="span"
            color={customerEmail.status === 'known' ? 'grey90' : 'grey70'}
          >
            {emailFieldText()}
          </Text.BodyText4>
          <Button
            kind="minimal"
            disabled={!canCopy}
            onClick={handleCopy}
            overrides={
              !canCopy && reason
                ? {
                    BaseButton: {
                      props: { 'aria-describedby': EMAIL_REASON_ID },
                    },
                  }
                : undefined
            }
          >
            <img
              src={icCopy}
              alt=""
              aria-hidden="true"
              width={16}
              height={16}
              className="mr-xs"
            />
            {t('copy')}
          </Button>
        </div>
        {reason && (
          <div id={EMAIL_REASON_ID} className="mt-xs">
            <Text.BodyText5 tag="span" color="grey70">
              {reason}
            </Text.BodyText5>
          </div>
        )}
      </div>

      <Form
        className="mt-m"
        onValid={() => setIsFormValid(true)}
        onInvalid={() => setIsFormValid(false)}
      >
        <Input
          key={`subject-${resetCount}`}
          name="subject"
          label={t('subject_label')}
          placeholder={t('subject_placeholder')}
          value={subject}
          onChange={event => setSubject(event.currentTarget.value)}
          className="mb-m"
          counterStyle="bare"
          validations={{ maxLength: SUBJECT_MAX_LENGTH }}
          validationErrors={{
            maxLength: validationT('max_length', {
              maxLength: SUBJECT_MAX_LENGTH,
            }),
          }}
        />
        <Input
          key={`message-${resetCount}`}
          name="message"
          componentClass="textarea"
          label={t('message_label')}
          placeholder={t('message_placeholder')}
          inputProps={{ rows: MESSAGE_ROWS }}
          value={message}
          onChange={event => setMessage(event.currentTarget.value)}
          className="mb-xs"
          counterStyle="bare"
          validations={{ maxLength: MESSAGE_MAX_LENGTH }}
          validationErrors={{
            maxLength: validationT('max_length', {
              maxLength: MESSAGE_MAX_LENGTH,
            }),
          }}
        />
      </Form>

      {/* Send-and-resolve callers only: name the second effect so the operator
          isn't surprised when the case closes and the action row unmounts after
          a button labelled only "Send". Send-only callers never render it. */}
      {resolvesRecord && (
        <Text.BodyText5 color="grey70" className="mt-xs">
          {t('resolve_note')}
        </Text.BodyText5>
      )}

      {/* Omitted when the operator's address is unknown — the sentence has no
          meaning without it. Deliberately NOT gated on customerEmail: Reply-To
          is the operator either way, so a masked recipient still gets it.
          Mirrors ResolveWithReplyModal. */}
      {operatorEmail && (
        <Text.BodyText5 color="grey70">
          {t('reply_to_footer', { email: operatorEmail })}
        </Text.BodyText5>
      )}

      {isError && (
        <div className={css({ marginTop: '12px' })}>
          <Notification
            kind="negative"
            size="full"
            closeable
            onClose={onResetError}
          >
            {errorMessage}
          </Notification>
        </div>
      )}
    </ModalContainer>
  )
}

export default SendMessageModal
